import React, { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useEvents } from '../hooks/useEvents';

const SchedulePage = () => {
  const { events: allEvents, loading } = useEvents();
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [filter, setFilter] = useState('all');
  const [timeFilter, setTimeFilter] = useState('upcoming'); // 'upcoming' or 'past'
  const [audienceFilter, setAudienceFilter] = useState('all');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [eventTypeFilter, setEventTypeFilter] = useState('all'); // 'in_person', 'virtual', 'hybrid'

  // Filter and sort events
  const filteredEvents = useMemo(() => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    return allEvents.filter(event => {
      const eventDate = new Date(event.end || event.start || event.date);
      const isPast = eventDate < today;
      
      // Time filter
      if (timeFilter === 'upcoming' && isPast) return false;
      if (timeFilter === 'past' && !isPast) return false;
      
      // Organization filter
      if (filter !== 'all' && event.org !== filter) return false;
      
      // Audience filter
      if (audienceFilter !== 'all' && event.event_audience !== audienceFilter) return false;
      
      // Category filter
      if (categoryFilter !== 'all' && event.event_category !== categoryFilter) return false;
      
      // Event type filter (in-person, virtual, hybrid)
      if (eventTypeFilter !== 'all' && event.event_type !== eventTypeFilter) return false;
      
      return true;
    }).sort((a, b) => {
      const dateA = new Date(a.start || a.date);
      const dateB = new Date(b.start || b.date);
      return timeFilter === 'past' ? dateB - dateA : dateA - dateB;
    });
  }, [filter, timeFilter, audienceFilter, categoryFilter, eventTypeFilter, allEvents]);

  // Set initial selected event
  useMemo(() => {
    if (!selectedEvent && filteredEvents.length > 0) {
      setSelectedEvent(filteredEvents[0].id);
    }
  }, [filteredEvents, selectedEvent]);

  const selectedEventData = filteredEvents.find(e => e.id === selectedEvent);

  const getOrgStyles = (org) => {
    switch (org?.toUpperCase()) {
      case 'UPCI':
        return 'bg-blue-100 text-blue-800 dark:bg-blue-900/50 dark:text-blue-300';
      case 'WPF':
        return 'bg-purple-100 text-purple-800 dark:bg-purple-900/50 dark:text-purple-300';
      case 'ALJC':
        return 'bg-red-100 text-red-800 dark:bg-red-900/50 dark:text-red-300';
      case 'AFF':
        return 'bg-green-100 text-green-800 dark:bg-green-900/50 dark:text-green-300';
      case 'AIM':
        return 'bg-orange-100 text-orange-800 dark:bg-orange-900/50 dark:text-orange-300';
      case 'PAW':
        return 'bg-teal-100 text-teal-800 dark:bg-teal-900/50 dark:text-teal-300';
      case 'INDEPENDENT':
      case 'INDEPENDENT CHURCHES':
        return 'bg-cyan-100 text-cyan-800 dark:bg-cyan-900/50 dark:text-cyan-300';
      default:
        return 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300';
    }
  };

  const formatEventDate = (event) => {
    const start = new Date(event.start || event.date);
    const end = event.end ? new Date(event.end) : null;
    
    if (end && start.toDateString() !== end.toDateString()) {
      return `${start.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })} - ${end.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}`;
    }
    return start.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 pt-24 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 pt-24">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <h1 className="font-display text-3xl md:text-4xl font-bold text-gray-900 dark:text-white mb-2">
          Event Schedules
        </h1>
        <p className="text-gray-600 dark:text-gray-400 max-w-3xl">
          Browse all conference schedules, past and upcoming events with detailed information.
        </p>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-16">
      {/* Filters */}
      <div className="mb-8 space-y-4">
        {/* Time Filter */}
        <div className="flex gap-2">
          <button
            onClick={() => setTimeFilter('upcoming')}
            className={`px-4 py-2 rounded-lg font-medium transition-colors ${
              timeFilter === 'upcoming'
                ? 'bg-primary text-white'
                : 'bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700'
            }`}
          >
            Upcoming Events
          </button>
          <button
            onClick={() => setTimeFilter('past')}
            className={`px-4 py-2 rounded-lg font-medium transition-colors ${
              timeFilter === 'past'
                ? 'bg-primary text-white'
                : 'bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700'
            }`}
          >
            Past Events
          </button>
        </div>

        {/* Additional Filters */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
          {/* Organization Filter */}
          <select
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            className="px-4 py-2 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-900 dark:text-white focus:ring-primary focus:border-primary text-sm"
          >
            <option value="all">All Organizations</option>
            <option value="UPCI">UPCI</option>
            <option value="WPF">WPF</option>
            <option value="ALJC">ALJC</option>
            <option value="AFF">AFF</option>
            <option value="AIM">AIM</option>
            <option value="PAW">PAW</option>
            <option value="Independent Churches">Independent Churches</option>
          </select>

          {/* Audience Filter */}
          <select
            value={audienceFilter}
            onChange={(e) => setAudienceFilter(e.target.value)}
            className="px-4 py-2 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-900 dark:text-white focus:ring-primary focus:border-primary text-sm"
          >
            <option value="all">All Audiences</option>
            <option value="general">General/All Ages</option>
            <option value="singles">Singles</option>
            <option value="married">Married Couples</option>
            <option value="youth">Youth</option>
            <option value="children">Children</option>
            <option value="seniors">Seniors</option>
            <option value="ministers">Ministers/Pastors</option>
            <option value="women">Women</option>
            <option value="men">Men</option>
            <option value="families">Families</option>
          </select>

          {/* Category Filter */}
          <select
            value={categoryFilter}
            onChange={(e) => setCategoryFilter(e.target.value)}
            className="px-4 py-2 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-900 dark:text-white focus:ring-primary focus:border-primary text-sm"
          >
            <option value="all">All Categories</option>
            <option value="conference">Conference</option>
            <option value="retreat">Retreat</option>
            <option value="camp">Camp</option>
            <option value="workshop">Workshop</option>
            <option value="seminar">Seminar</option>
            <option value="convention">Convention</option>
            <option value="rally">Rally</option>
            <option value="revival">Revival</option>
            <option value="training">Training</option>
            <option value="fellowship">Fellowship</option>
            <option value="outreach">Outreach</option>
            <option value="missions">Missions</option>
          </select>

          {/* Event Type Filter */}
          <select
            value={eventTypeFilter}
            onChange={(e) => setEventTypeFilter(e.target.value)}
            className="px-4 py-2 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-900 dark:text-white focus:ring-primary focus:border-primary text-sm"
          >
            <option value="all">All Event Types</option>
            <option value="in_person">In-Person Only</option>
            <option value="virtual">Virtual Only</option>
            <option value="hybrid">Hybrid Events</option>
          </select>
        </div>

        {/* Active Filters Display */}
        {(filter !== 'all' || audienceFilter !== 'all' || categoryFilter !== 'all' || eventTypeFilter !== 'all') && (
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-sm text-gray-600 dark:text-gray-400">Active filters:</span>
            {filter !== 'all' && (
              <span className="inline-flex items-center px-2 py-1 bg-primary/10 text-primary rounded text-xs">
                Org: {filter}
                <button onClick={() => setFilter('all')} className="ml-1 hover:text-primary-dark">×</button>
              </span>
            )}
            {audienceFilter !== 'all' && (
              <span className="inline-flex items-center px-2 py-1 bg-primary/10 text-primary rounded text-xs capitalize">
                {audienceFilter}
                <button onClick={() => setAudienceFilter('all')} className="ml-1 hover:text-primary-dark">×</button>
              </span>
            )}
            {categoryFilter !== 'all' && (
              <span className="inline-flex items-center px-2 py-1 bg-primary/10 text-primary rounded text-xs capitalize">
                {categoryFilter}
                <button onClick={() => setCategoryFilter('all')} className="ml-1 hover:text-primary-dark">×</button>
              </span>
            )}
            {eventTypeFilter !== 'all' && (
              <span className="inline-flex items-center px-2 py-1 bg-primary/10 text-primary rounded text-xs">
                {eventTypeFilter === 'in_person' ? 'In-Person' : eventTypeFilter === 'virtual' ? 'Virtual' : 'Hybrid'}
                <button onClick={() => setEventTypeFilter('all')} className="ml-1 hover:text-primary-dark">×</button>
              </span>
            )}
            <button
              onClick={() => {
                setFilter('all');
                setAudienceFilter('all');
                setCategoryFilter('all');
                setEventTypeFilter('all');
              }}
              className="text-xs text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 underline"
            >
              Clear all
            </button>
          </div>
        )}
      </div>

      {/* Events Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* Event List */}
        <div className="lg:col-span-1">
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-md border border-gray-200 dark:border-gray-700 overflow-hidden">
            <div className="px-4 py-3 bg-gray-50 dark:bg-gray-700/50 border-b border-gray-200 dark:border-gray-700">
              <h3 className="text-lg font-medium text-gray-900 dark:text-white">
                {timeFilter === 'upcoming' ? 'Upcoming' : 'Past'} Events ({filteredEvents.length})
              </h3>
            </div>
            <div className="divide-y divide-gray-200 dark:divide-gray-700 max-h-[600px] overflow-y-auto">
              {filteredEvents.length > 0 ? (
                filteredEvents.map((event) => (
                  <button
                    key={event.id}
                    onClick={() => setSelectedEvent(event.id)}
                    className={`w-full text-left px-4 py-4 hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors ${
                      selectedEvent === event.id ? 'bg-primary/10 dark:bg-primary/20 border-l-4 border-primary' : ''
                    }`}
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <h4 className="text-sm font-medium text-gray-900 dark:text-white mb-1">
                          {event.title || event.event}
                        </h4>
                        <p className="text-xs text-gray-500 dark:text-gray-400 mb-2">
                          {formatEventDate(event)}
                        </p>
                        <div className="flex items-center gap-2">
                          <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${getOrgStyles(event.org)}`}>
                            {event.org}
                          </span>
                          <span className="text-xs text-gray-500 dark:text-gray-400">{event.city}</span>
                        </div>
                      </div>
                    </div>
                  </button>
                ))
              ) : (
                <div className="px-4 py-12 text-center text-gray-500 dark:text-gray-400">
                  <p>No {timeFilter} events found.</p>
                  <button
                    onClick={() => {
                      setFilter('all');
                      setAudienceFilter('all');
                      setCategoryFilter('all');
                      setEventTypeFilter('all');
                    }}
                    className="mt-2 text-primary hover:text-primary-dark text-sm"
                  >
                    Clear all filters
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Event Details */}
        <div className="lg:col-span-2">
          {selectedEventData ? (
            <div className="bg-white dark:bg-gray-800 rounded-xl shadow-md border border-gray-200 dark:border-gray-700 overflow-hidden">
              <div className="relative h-48 bg-gradient-to-r from-gray-700 to-gray-900">
                {selectedEventData.image_url && (
                  <img 
                    src={selectedEventData.image_url} 
                    alt={selectedEventData.title || selectedEventData.event}
                    className="absolute inset-0 w-full h-full object-cover"
                  />
                )}
                <div className="absolute inset-0 bg-black opacity-40"></div>
                <div className="relative h-full flex flex-col justify-end p-6 text-white">
                  <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium mb-2 w-fit ${getOrgStyles(selectedEventData.org)}`}>
                    {selectedEventData.org}
                  </span>
                  <h2 className="text-2xl font-bold">{selectedEventData.title || selectedEventData.event}</h2>
                  <p className="text-sm opacity-90 mt-1">{selectedEventData.venue}, {selectedEventData.city}</p>
                </div>
              </div>

              <div className="p-6">
                {/* Event Info */}
                <div className="grid grid-cols-2 gap-4 mb-6">
                  <div>
                    <h4 className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-1">Date</h4>
                    <p className="text-sm text-gray-900 dark:text-white">{formatEventDate(selectedEventData)}</p>
                  </div>
                  <div>
                    <h4 className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-1">Status</h4>
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                      selectedEventData.status === 'confirmed' 
                        ? 'bg-green-100 text-green-800 dark:bg-green-900/50 dark:text-green-300' 
                        : selectedEventData.status === 'completed'
                        ? 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300'
                        : 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/50 dark:text-yellow-300'
                    }`}>
                      {selectedEventData.status === 'confirmed' ? 'Confirmed' : selectedEventData.status === 'completed' ? 'Completed' : 'Tentative'}
                    </span>
                  </div>
                  <div>
                    <h4 className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-1">Venue</h4>
                    <p className="text-sm text-gray-900 dark:text-white">{selectedEventData.venue}</p>
                  </div>
                  <div>
                    <h4 className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-1">Airport</h4>
                    <p className="text-sm text-gray-900 dark:text-white">{selectedEventData.airport || 'N/A'}</p>
                  </div>
                </div>

                {/* Description */}
                {selectedEventData.description && (
                  <div className="mb-6">
                    <h4 className="text-sm font-medium text-gray-900 dark:text-white mb-2">About</h4>
                    <p className="text-sm text-gray-600 dark:text-gray-400">{selectedEventData.description}</p>
                  </div>
                )}

                {/* Schedule/Notes */}
                {selectedEventData.notes && (
                  <div className="mb-6">
                    <h4 className="text-sm font-medium text-gray-900 dark:text-white mb-2">Schedule & Notes</h4>
                    <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-4">
                      <pre className="text-xs text-gray-700 dark:text-gray-300 whitespace-pre-wrap font-sans leading-relaxed">
                        {selectedEventData.notes}
                      </pre>
                    </div>
                  </div>
                )}

                {/* Hotels */}
                {selectedEventData.hotels && selectedEventData.hotels.length > 0 && (
                  <div className="mb-6">
                    <h4 className="text-sm font-medium text-gray-900 dark:text-white mb-2">Recommended Hotels</h4>
                    <div className="space-y-2">
                      {selectedEventData.hotels.map((hotel, index) => (
                        <div key={index} className="flex justify-between items-center p-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
                          <div>
                            <p className="text-sm font-medium text-gray-900 dark:text-white">{hotel.name}</p>
                            <p className="text-xs text-gray-500 dark:text-gray-400">{hotel.distance} • {hotel.note}</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Actions */}
                <div className="flex gap-3">
                  <Link
                    to={`/events/${selectedEventData.id}`}
                    className="flex-1 inline-flex justify-center items-center px-4 py-2 text-sm font-medium rounded-lg text-white bg-primary hover:bg-primary-dark transition-colors"
                  >
                    View Full Details
                  </Link>
                  {selectedEventData.website && (
                    <a
                      href={selectedEventData.website}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex-1 inline-flex justify-center items-center px-4 py-2 border border-gray-300 dark:border-gray-600 text-sm font-medium rounded-lg text-gray-700 dark:text-gray-200 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600 transition-colors"
                    >
                      Visit Website
                    </a>
                  )}
                </div>
              </div>
            </div>
          ) : (
            <div className="bg-white dark:bg-gray-800 rounded-xl shadow-md border border-gray-200 dark:border-gray-700 p-12 text-center">
              <svg className="mx-auto h-12 w-12 text-gray-400 dark:text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
              </svg>
              <h3 className="mt-2 text-sm font-medium text-gray-900 dark:text-white">No event selected</h3>
              <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">Select an event from the list to view details</p>
            </div>
          )}
        </div>
      </div>

      {/* Info Banner */}
      <div className="mt-8 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
        <div className="flex">
          <div className="flex-shrink-0">
            <svg className="h-5 w-5 text-blue-500 dark:text-blue-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
            </svg>
          </div>
          <div className="ml-3">
            <p className="text-sm text-blue-700 dark:text-blue-300">
              Schedules are subject to change. Please check event details for the most up-to-date information.
            </p>
          </div>
        </div>
      </div>
      </div>
    </div>
  );
};

export default SchedulePage;
